import { algoliasearch } from "algoliasearch";
import { find, flattenDeep, has, isNil, isObject, memoize } from "lodash-es";
import { makeAutoObservable, runInAction } from "mobx";

import { ALGOLIA_SEARCH_KEY } from "../../common/environment";
import { PageNotFoundError } from "../../common/errors";
import { logger } from "../../common/logger";
import type { BaseStoreType } from "../../common/types/BaseStoreType";
import type { CategoryContext, CategoryInfo, CategoryQuery, ManualShortcut } from "../../common/types/categoryTypes";
import { keepIdOnly } from "../../common/utils/segmentUtils";
import theme from "../../themes/theme";
import authStore from "../auth/authStore";
import productStore from "../product/productStore";

import { searchCategoryRescursive, searchForCategoryProducts, setHiddenStatus, updateSkusForCategory } from "./categoryUtils";

type CategoryStoreType = BaseStoreType & {
   categories: CategoryInfo[];
   manualShortcuts: ManualShortcut[];
   mobile: {
      visible: boolean;
      path: string[];
   };
   showMenu(setTo: boolean): void;
   findCategoryByUrlKey(urlKeys: CategoryQuery): CategoryContext | null;
   findCategoryKeysBySku(sku: string): CategoryQuery[];
   hideEmptyCategories(): void;
   updateCategorySkus(): Promise<void>;
};

const searchClient = theme.searchConfig?.algoliaAppId ? algoliasearch(theme.searchConfig.algoliaAppId, ALGOLIA_SEARCH_KEY) : null;

const { categorySearchIndexName } = theme.searchConfig || {};

const categoryStore: CategoryStoreType = makeAutoObservable({
   categories: [],
   manualShortcuts: [],
   mobile: {
      visible: false,
      path: []
   },

   // Used for the mobile menu that slides in
   showMenu: (setTo) => {
      categoryStore.mobile.visible = setTo;
   },

   updateCategorySkus: async () => {
      if (categorySearchIndexName && searchClient) {
         const viewableSkus = productStore.viewableProducts.map((p) => p.sku);
         const company = authStore.getCurrentCompany();
         const customerGroup = company ? keepIdOnly(company.customerGroup) : "default";

         try {
            const skusAndPaths = await searchForCategoryProducts(
               viewableSkus,
               searchClient,
               categorySearchIndexName,
               customerGroup
            );
            runInAction(() => {
               for (const category of categoryStore.categories) {
                  updateSkusForCategory(category, skusAndPaths);
               }
            });

            logger.log("categories", `Found ${skusAndPaths.length} skus for categories`);
         } catch (error) {
            logger.error("categories", "Error updating category skus:", error);
         }
      }
   },

   findCategoryByUrlKey: (urlKeys) => {
      const levels: CategoryInfo[] = [];
      let category = find(categoryStore.categories, { url_key: urlKeys.cat1 });
      if (isNil(category)) {
         throw new PageNotFoundError();
      }
      levels.push(category);

      if (has(urlKeys, "cat2")) {
         const category2 = find(category.children, { url_key: urlKeys.cat2 });
         if (!isNil(category2)) {
            category = category2;
            levels.push(category2);
         } else {
            throw new PageNotFoundError();
         }
      }

      if (has(urlKeys, "cat3")) {
         const category3 = find(category.children, { url_key: urlKeys.cat3 });
         if (!isNil(category3)) {
            category = category3;
            levels.push(category3);
         } else {
            throw new PageNotFoundError();
         }
      }

      return {
         category,
         levels
      };
   },

   findCategoryKeysBySku: memoize((lookForSku) => {
      return flattenDeep(categoryStore.categories.map((cat) => searchCategoryRescursive(lookForSku, cat, 1))).filter(isObject);
   }),

   hideEmptyCategories: () => {
      for (let i = 0; i < categoryStore.categories.length; i++) {
         setHiddenStatus(categoryStore.categories[i]);
      }
   },
   clearCompanySpecificData: () => {}
} satisfies CategoryStoreType);

export default categoryStore;
